/**
 * Excel formula security: blocked-function + DDE screening.
 *
 * Ported from the reverse-engineered Claude-for-Excel reference
 * (docs/reverse-engineered-claude/claude-for-excel/tool-implementations.ts, the
 * "FORMULA SECURITY" section). Some Excel formula functions can reach the
 * network, the file system, or external resources (=WEBSERVICE, =IMPORTDATA,
 * =RTD, ...), and DDE prefixes (a pipe inside a formula) can launch processes.
 * A prompt-injected or malicious cell could use these to exfiltrate sheet data
 * on recalculation, so any formula the agent writes is screened first and a
 * blocked formula is rejected exactly like the reference does
 * (IllegalAccessError at set_cell_range and the execute_office_js escape hatch).
 *
 * Note on precision: the reference instruments the live RequestContext with a
 * Proxy and validates the actual value at write time on the formula setters. We
 * cannot wrap the Office.js object graph that way without risking the batched
 * Excel.run, so we screen at two static points instead: every formula value
 * passed to set_cell_range, and every formula-shaped string literal in the
 * execute_office_js code (not arbitrary mentions of a name, so reading a cell
 * whose text happens to contain "INDIRECT" no longer false-positives). The one
 * case this misses that a runtime Proxy would catch is a formula assembled by
 * string concatenation at run time (for example "=WEB" + "SERVICE("); the
 * set_cell_range path, which is the model's normal write route, is screened on
 * the resolved value and has no such gap.
 */

// Functions blocked from being written to cells (network / external / exec).
// Exact list from the reference.
export const BLOCKED_FORMULA_FUNCTIONS = [
  'WEBSERVICE', 'IMPORTDATA', 'INDIRECT', 'IMPORTXML', 'IMPORTHTML',
  'IMPORTFEED', 'FILTERXML', 'RTD', 'REGISTER.ID', 'CALL', 'EVALUATE',
  'FORMULA', 'IMAGE', 'FILES', 'DIRECTORY', 'DDE', 'FOPEN', 'FWRITE',
  'FCLOSE', 'INFO', 'STOCKHISTORY', 'STOCKSERIES', 'TRANSLATE',
  'CUBEKPIMEMBER', 'CUBEMEMBER', 'CUBEMEMBERPROPERTY', 'CUBERANKEDMEMBER',
  'CUBESET', 'CUBESETCOUNT', 'CUBEVALUE', 'EXEC',
];

// Prefixes that indicate a formula (also the DDE injection vectors).
export const FORMULA_PREFIXES = ['=', '+', '-', '@', '{='];

// Precompiled per-function call matchers: the function name (with optional
// Excel _xlfn./_xlws. internal prefixes handled by the word boundary) followed
// by optional whitespace and an opening paren.
const FUNCTION_MATCHERS = BLOCKED_FORMULA_FUNCTIONS.map((fn) => {
  const escaped = fn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return { fn, re: new RegExp('\\b' + escaped + '\\s*\\(', 'i') };
});

/**
 * Return the blocked function names referenced as calls in a string. Used both
 * to validate a formula value and to scan AI-generated execute_office_js code.
 * @param {string} text
 * @returns {string[]} unique blocked function names found
 */
export function findBlockedFunctions(text) {
  if (typeof text !== 'string' || !text) return [];
  const found = [];
  for (const { fn, re } of FUNCTION_MATCHERS) {
    if (re.test(text) && !found.includes(fn)) found.push(fn);
  }
  return found;
}

/**
 * Whether a single cell formula value contains a blocked function or a DDE
 * injection (a pipe character inside a formula). Mirrors the reference's
 * containsBlockedFormula: only strings that look like a formula are screened.
 * @param {string} formula
 * @returns {boolean}
 */
export function containsBlockedFormula(formula) {
  if (typeof formula !== 'string') return false;
  const trimmed = formula.trim();
  if (!trimmed || !FORMULA_PREFIXES.some((p) => trimmed.startsWith(p))) return false;
  // DDE injection: a pipe inside a formula is the classic process-launch marker.
  if (trimmed.includes('|')) return true;
  return findBlockedFunctions(trimmed).length > 0;
}

/**
 * Assert a list of formula strings are all safe; throw the reference's
 * IllegalAccessError naming the offending functions if not. Pass the formula
 * values you are about to write (set_cell_range) so the throw matches the
 * reference's behavior.
 * @param {string[]} formulas
 */
export function assertFormulasAllowed(formulas) {
  const blocked = new Set();
  for (const f of formulas) {
    if (typeof f !== 'string') continue;
    const trimmed = f.trim();
    if (!trimmed || !FORMULA_PREFIXES.some((p) => trimmed.startsWith(p))) continue;
    if (trimmed.includes('|')) blocked.add('DDE');
    for (const fn of findBlockedFunctions(trimmed)) blocked.add(fn);
  }
  if (blocked.size > 0) {
    throw new Error(
      `IllegalAccessError: Method argument contains blocked formula function(s): ${[...blocked].join(', ')}. ` +
      'These functions can reach the network or external resources and are not allowed.'
    );
  }
}

/**
 * Collect every string in a value being assigned to an Excel range's
 * `formulas` / `formulasLocal` setter. Office.js accepts a 2-D array (the
 * normal shape), but tolerates a 1-D array or a bare scalar too, so flatten all
 * three into the list of candidate formula strings. Non-strings are skipped.
 * @param {*} value - The value being assigned to formulas / formulasLocal
 * @returns {string[]} every string found in the assignment
 */
function collectFormulaStrings(value) {
  const out = [];
  const visit = (v) => {
    if (Array.isArray(v)) {
      for (const item of v) visit(item);
    } else if (typeof v === 'string') {
      out.push(v);
    }
  };
  visit(value);
  return out;
}

/**
 * Screen a value about to be written to an Excel range's `formulas` /
 * `formulasLocal` setter and throw the reference's IllegalAccessError if any
 * cell carries a blocked function or a DDE pipe. This is the runtime write
 * boundary that the static execute_office_js code scan cannot cover: a formula
 * assembled by string concatenation at run time (for example
 * "=WEB" + "SERVICE(") is only visible as a resolved value here, so screening
 * the assigned grid closes that bypass. Fails closed (throws) on a blocked
 * formula; legitimate formulas pass through untouched.
 * @param {*} value - The value being assigned to formulas / formulasLocal
 */
export function assertFormulaAssignmentAllowed(value) {
  assertFormulasAllowed(collectFormulaStrings(value));
}

// Matches JS string literals (single, double, or backtick quoted), capturing
// the inner text in group 2. The \1 backreference keeps the closing quote
// matched to the opening one and \\. lets escaped quotes pass through.
const STRING_LITERAL_RE = /(['"`])((?:\\.|(?!\1)[\s\S])*?)\1/g;

/**
 * Scan AI-generated execute_office_js code for blocked formula writes (the
 * escape-hatch equivalent of the set_cell_range screen). Only formula-shaped
 * string literals are screened (a literal that, once unescaped of its trivial
 * \\" / \\' escapes, starts with a formula prefix), so code that merely reads a
 * cell or comment containing a function name no longer false-positives. Throws
 * with the reference's IllegalAccessError if any such literal carries a blocked
 * function or a DDE pipe.
 * @param {string} code
 */
export function assertCodeFreeOfBlockedFormulas(code) {
  if (typeof code !== 'string' || !code) return;
  const blocked = new Set();
  let match;
  STRING_LITERAL_RE.lastIndex = 0;
  while ((match = STRING_LITERAL_RE.exec(code)) !== null) {
    // Undo the basic backslash escapes so "=\"INDIRECT...\"" is screened as the
    // formula it becomes once written, then reuse the single-value check.
    const literal = match[2].replace(/\\(['"`\\])/g, '$1');
    const trimmed = literal.trim();
    if (!trimmed || !FORMULA_PREFIXES.some((p) => trimmed.startsWith(p))) continue;
    if (trimmed.includes('|')) blocked.add('DDE');
    for (const fn of findBlockedFunctions(trimmed)) blocked.add(fn);
  }
  if (blocked.size > 0) {
    throw new Error(
      `IllegalAccessError: execute_office_js code contains blocked formula function(s): ${[...blocked].join(', ')}. ` +
      'Writing these formulas is not allowed (network / external access).'
    );
  }
}
